import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import { stableSerialize } from "@shared/lib/http/stableSerialize";
import { EntriesPaginatedSchema, SearchEntriesBrowseResponse } from "@models/entry";
import {
  ENTRY_BROWSE_INCLUDES,
  ENTRY_BROWSE_PER_PAGE,
  EntrySearchParams,
  getEntrySearchBody,
} from "./entriesBrowse.utils";

export const entriesApi = createApi({
  reducerPath: "entriesApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["entries", "entriesBrowse"],
  endpoints: (b) => ({

    searchEntriesBrowse: b.query<SearchEntriesBrowseResponse, EntrySearchParams>({
      query: searchEntriesBrowseQuery,
      serializeQueryArgs: searchEntriesBrowseSerializeQueryArgs,
      transformResponse: parseEntriesBrowseResponse,
      providesTags: searchEntriesBrowseProvidesTags,
    }),

  }),
});

export const {
  useSearchEntriesBrowseQuery,
} = entriesApi;


/**
 * Validates a paginated entries browse API payload.
 * @param response unknown - Raw response body from axiosBaseQuery.
 * @returns SearchEntriesBrowseResponse - Parsed paginated entries payload with rows and cursor meta.
 */
function parseEntriesBrowseResponse(response: unknown): SearchEntriesBrowseResponse {

  return EntriesPaginatedSchema.parse(response);
}


/**
 * Builds a stable cache key for entries browse queries with object args.
 * @param endpointName string - RTK Query endpoint name.
 * @param queryArgs EntrySearchParams - Browse filters, includes, sort, and pagination.
 * @returns string - Serialized cache key for the query args.
 */
function searchEntriesBrowseSerializeQueryArgs({ endpointName, queryArgs }: { endpointName: string; queryArgs: EntrySearchParams }): string {
  const { useCache: _omit, ...cacheKeyArgs } = queryArgs;

  return `${endpointName}:${stableSerialize(cacheKeyArgs)}`;
}


/**
 * Builds the axios request for entries browse search.
 * @param params EntrySearchParams - Account id, filters, includes, sort, and pagination.
 * @returns object - URL, method, and POST body for entries/search.
 */
function searchEntriesBrowseQuery({ accountId, filter, include, per_page, sort, useCache, ...rest }: EntrySearchParams) {

  return {
    url: "entries/search",
    method: "POST" as const,
    data: getEntrySearchBody({
      accountId,
      filter: { account_id: accountId, ...filter },
      include: include ?? ENTRY_BROWSE_INCLUDES,
      sort: sort ?? ["-id"],
      per_page: per_page ?? ENTRY_BROWSE_PER_PAGE,
      useCache,
      ...rest,
    }),
  };
}


/**
 * Resolves RTK Query cache tags for entries browse by account id.
 * @param _res SearchEntriesBrowseResponse | undefined - Successful query result (unused).
 * @param _err unknown - Query error (unused).
 * @param arg EntrySearchParams - Browse args containing accountId.
 * @returns Array - Cache tag keyed by account id.
 */
function searchEntriesBrowseProvidesTags(_res: SearchEntriesBrowseResponse | undefined, _err: unknown, arg: EntrySearchParams) {

  return [{ type: "entriesBrowse" as const, id: arg.accountId }];
}
